import {
  AUTO_RELOAD_KEY,
  describeResponse,
  readBuildMeta,
  type AssetProbe,
  type ErrorReportInput,
} from "@shared/errorReport";
import { assetCandidates } from "@shared/chunkCache";

const ACTIVE_STORE_KEY = "active_store_id";
const PROBE_TIMEOUT_MS = 3_000;
const MAX_PROBES = 3;

function safeRead(read: () => string | null): string | null {
  try {
    return read();
  } catch {
    return null;
  }
}

/** Everything about the crash the page knows right now, without touching the network. */
export function snapshotDiagnostics(error: Error, caught: { at: number; componentStack?: string }): ErrorReportInput {
  const reloadedAt = Number(safeRead(() => sessionStorage.getItem(AUTO_RELOAD_KEY)) ?? 0);
  return {
    name: error.name,
    message: error.message,
    stack: error.stack,
    componentStack: caught.componentStack,
    path: `${window.location.pathname}${window.location.search}`,
    at: caught.at,
    build: document.querySelector<HTMLMetaElement>('meta[name="app-build"]')?.content || null,
    storeId: safeRead(() => localStorage.getItem(ACTIVE_STORE_KEY)),
    reloadedAt: reloadedAt > 0 ? reloadedAt : null,
    online: navigator.onLine,
    userAgent: navigator.userAgent,
  };
}

async function timed(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function probeAsset(url: string): Promise<AssetProbe> {
  const probe: AssetProbe = { url: new URL(url).pathname, cached: "?", network: "?" };
  try {
    const cached = await timed(url, { cache: "only-if-cached", mode: "same-origin" });
    // only-if-cached answers a synthetic 504 when the browser holds nothing.
    probe.cached = cached.status === 504 ? "tiada" : describeResponse(cached.status, cached.headers.get("content-type"));
  } catch {
    probe.cached = "tak dapat dibaca";
  }
  try {
    const network = await timed(url, { cache: "no-store" });
    probe.network = describeResponse(network.status, network.headers.get("content-type"));
  } catch (err) {
    probe.network = `gagal (${err instanceof Error ? err.name : "ralat"})`;
  }
  return probe;
}

/**
 * The two answers that decide a loading failure: what this browser holds for the
 * file that failed against what the server sends now, and whether the server is
 * still on the build this tab is running.
 */
export async function probeDiagnostics(error: Error): Promise<Pick<ErrorReportInput, "probes" | "serverBuild">> {
  const urls = assetCandidates(error.message, [], window.location.origin).slice(0, MAX_PROBES);
  const [probes, serverBuild] = await Promise.all([
    Promise.all(urls.map(probeAsset)),
    timed("/", { cache: "no-store" })
      .then((res) => res.text())
      .then(readBuildMeta)
      .catch(() => null),
  ]);
  return { probes, serverBuild };
}
